import { destroyAllObjectProperties } from '@/Utils';
import { c } from 'ttag';
import { confirmDialog, PIN_NOTE_COMMAND, STAR_NOTE_COMMAND, } from '@standardnotes/ui-services';
import { StringArchiveLockedNotesAttempt, StringDeleteItemsPermanentlyTitle, StringDeleteLockedNotesAttempt, StringDeleteNotes, StringEmptyTrash, StringTrashItemsTitle, } from '@/Constants/Strings';
import { ContentType, SNTag, PrefKey, ApplicationEvent, MutationType, PrefDefaults, LocalPrefKey, noteTypeForEditorIdentifier, pluralize, NoteType, } from '@standardnotes/snjs';
import { makeObservable, observable, action, computed, runInAction, reaction } from 'mobx';
import { AbstractViewController } from '../Abstract/AbstractViewController';
import { CrossControllerEvent } from '../CrossControllerEvent';
import { addToast, dismissToast, ToastType } from '@standardnotes/toast';
import { createNoteExport } from '../../Utils/NoteExportUtils';
import { downloadOrShareBlobBasedOnPlatform } from '../../Utils/DownloadOrShareBasedOnPlatform';
const jtString = (value) => (Array.isArray(value) ? value.join('') : String(value));
export class NotesController extends AbstractViewController {
    constructor(application, _isGlobalSpellcheckEnabled, _getItemTags) {
        super(application.events);
        this.application = application;
        this._isGlobalSpellcheckEnabled = _isGlobalSpellcheckEnabled;
        this._getItemTags = _getItemTags;
        this.contextMenuOpen = false;
        this.contextMenuClickLocation = { x: 0, y: 0 };
        this.contextMenuMaxHeight = 'auto';
        this.showProtectedWarning = false;
        this.shouldShowSuperExportModal = false;
        this.commandRegisterDisposers = [];
        this.setContextMenuOpen = (open) => {
            this.contextMenuOpen = open;
        };
        this.togglePinSelectedNotes = () => {
            const notes = this.selectedNotes;
            const pinned = notes.some((note) => note.pinned);
            if (!pinned) {
                this.setPinSelectedNotes(true);
            }
            else {
                this.setPinSelectedNotes(false);
            }
        };
        this.toggleStarSelectedNotes = () => {
            const notes = this.selectedNotes;
            const starred = notes.some((note) => note.starred);
            if (!starred) {
                this.setStarSelectedNotes(true);
            }
            else {
                this.setStarSelectedNotes(false);
            }
        };
        this.showSuperExportModal = () => {
            this.shouldShowSuperExportModal = true;
        };
        this.closeSuperExportModal = () => {
            this.shouldShowSuperExportModal = false;
        };
        // gets attribute info about the given notes in a single loop
        this.getNotesInfo = (notes) => {
            let pinned = false, unpinned = false, starred = false, unstarred = false, trashed = false, notTrashed = false, archived = false, unarchived = false, hiddenPreviews = 0, unhiddenPreviews = 0, locked = 0, unlocked = 0, protecteds = 0, unprotected = 0;
            for (let i = 0; i < notes.length; i++) {
                const note = notes[i];
                if (!note) {
                    continue;
                }
                if (note.pinned) {
                    pinned = true;
                }
                else {
                    unpinned = true;
                }
                if (note.starred) {
                    starred = true;
                }
                else {
                    unstarred = true;
                }
                if (note.trashed) {
                    trashed = true;
                }
                else {
                    notTrashed = true;
                }
                if (note.archived) {
                    archived = true;
                }
                else {
                    unarchived = true;
                }
                if (note.hidePreview) {
                    hiddenPreviews++;
                }
                else {
                    unhiddenPreviews++;
                }
                if (note.locked) {
                    locked++;
                }
                else {
                    unlocked++;
                }
                if (note.protected) {
                    protecteds++;
                }
                else {
                    unprotected++;
                }
            }
            return {
                pinned,
                unpinned,
                starred,
                unstarred,
                trashed,
                notTrashed,
                archived,
                unarchived,
                hidePreviews: hiddenPreviews > unhiddenPreviews,
                locked: locked > unlocked,
                protect: protecteds > unprotected,
            };
        };
        this.downloadSelectedNotes = async () => {
            const notes = this.selectedNotes;
            if (notes.length === 0) {
                return;
            }
            const noteWord = pluralize(notes.length, c('B3.Notes.NoteActions.Label').t `note`, c('B3.Notes.NoteActions.Label').t `notes`);
            const toast = addToast({
                type: ToastType.Progress,
                message: jtString(c('B3.Notes.NoteActions.Info').jt `Exporting ${notes.length} ${noteWord}...`),
            });
            try {
                const result = await createNoteExport(this.application, notes);
                if (!result) {
                    return;
                }
                const { blob, fileName } = result;
                void downloadOrShareBlobBasedOnPlatform({
                    archiveService: this.application.archiveService,
                    platform: this.application.platform,
                    mobileDevice: this.application.mobileDevice,
                    blob: blob,
                    filename: fileName,
                    isNativeMobileWeb: this.application.isNativeMobileWeb(),
                });
                dismissToast(toast);
            }
            catch (error) {
                console.error(error);
                addToast({
                    type: ToastType.Error,
                    message: c('B3.Notes.NoteActions.Error').t `Could not export notes`,
                });
                dismissToast(toast);
            }
        };
        this.exportSelectedNotes = () => {
            const notes = this.selectedNotes;
            const hasSuperNote = notes.some((note) => note.noteType === NoteType.Super);
            if (hasSuperNote) {
                this.showSuperExportModal();
                return;
            }
            this.downloadSelectedNotes().catch(console.error);
        };
        this.duplicateSelectedNotes = async () => {
            const notes = this.selectedNotes;
            await Promise.all(notes.map((note) => this.application.mutator
                .duplicateItem(note)
                .then((duplicated) => addToast({
                type: ToastType.Regular,
                message: jtString(c('B3.Notes.NoteActions.Info').jt `Duplicated note "${duplicated.title}"`),
                actions: [
                    {
                        label: c('B3.Notes.NoteActions.Action').t `Open`,
                        handler: (toastId) => {
                            this.application.itemListController.selectUuids([duplicated.uuid], true).catch(console.error);
                            dismissToast(toastId);
                        },
                    },
                ],
                autoClose: true,
            }))
                .catch(console.error)));
            void this.application.sync.sync();
        };
        makeObservable(this, {
            contextMenuOpen: observable,
            showProtectedWarning: observable,
            shouldShowSuperExportModal: observable,
            selectedNotes: computed,
            firstSelectedNote: computed,
            selectedNotesCount: computed,
            trashedNotesCount: computed,
            setContextMenuOpen: action,
            setContextMenuClickLocation: action,
            setShowProtectedWarning: action,
            unselectNotes: action,
            showSuperExportModal: action,
            closeSuperExportModal: action,
        });
        this.shouldLinkToParentFolders = application.preferences.getValue(PrefKey.NoteAddToParentFolders, PrefDefaults[PrefKey.NoteAddToParentFolders]);
        application.events.addEventHandler(this, ApplicationEvent.PreferencesChanged);
        application.events.addEventHandler(this, CrossControllerEvent.UnselectAllNotes);
        this.disposers.push(reaction(() => this.selectedNotesCount, (notes_count) => {
            this.disposeCommandRegisters();
            const noteCommandTarget = notes_count === 1
                ? c('B3.Notes.NoteActions.Label').t `current note`
                : c('B3.Notes.NoteActions.Label').t `selected note(s)`;
            this.commandRegisterDisposers.push(application.commands.add('pin-current', jtString(c('B3.Notes.NoteActions.Action').jt `Pin ${noteCommandTarget}`), () => this.setPinSelectedNotes(true), 'unpin'), application.commands.add('unpin-current', jtString(c('B3.Notes.NoteActions.Action').jt `Unpin ${noteCommandTarget}`), () => this.setPinSelectedNotes(false), 'pin'), application.commands.add('star-current', jtString(c('B3.Notes.NoteActions.Action').jt `Star ${noteCommandTarget}`), () => this.setStarSelectedNotes(true), 'star'), application.commands.add('unstar-current', jtString(c('B3.Notes.NoteActions.Action').jt `Unstar ${noteCommandTarget}`), () => this.setStarSelectedNotes(false), 'star'), application.commands.add('archive-current', jtString(c('B3.Notes.NoteActions.Action').jt `Archive ${noteCommandTarget}`), () => this.setArchiveSelectedNotes(true), 'archive'), application.commands.add('unarchive-current', jtString(c('B3.Notes.NoteActions.Action').jt `Unarchive ${noteCommandTarget}`), () => this.setArchiveSelectedNotes(false), 'unarchive'), application.commands.add('restore-current', jtString(c('B3.Notes.NoteActions.Action').jt `Restore ${noteCommandTarget}`), () => this.setTrashSelectedNotes(false), 'restore'), application.commands.add('trash-current', jtString(c('B3.Notes.NoteActions.Action').jt `Trash ${noteCommandTarget}`), () => this.setTrashSelectedNotes(true), 'trash'), application.commands.add('delete-current', jtString(c('B3.Notes.NoteActions.Action').jt `Delete ${noteCommandTarget} permanently`), () => this.deleteNotesPermanently(), 'trash'), application.commands.add('export-current', jtString(c('B3.Notes.NoteActions.Action').jt `Export ${noteCommandTarget}`), this.exportSelectedNotes, 'download'), application.commands.add('duplicate-current', jtString(c('B3.Notes.NoteActions.Action').jt `Duplicate ${noteCommandTarget}`), this.duplicateSelectedNotes, 'copy'));
        }));
        this.disposers.push(application.keyboardService.addCommandHandler({
            command: PIN_NOTE_COMMAND,
            category: 'Current note',
            description: c('B3.Notes.NoteActions.Action').t `Pin/unpin selected note(s)`,
            onKeyDown: this.togglePinSelectedNotes,
        }), application.keyboardService.addCommandHandler({
            command: STAR_NOTE_COMMAND,
            category: 'Current note',
            description: c('B3.Notes.NoteActions.Action').t `Star/unstar selected note(s)`,
            onKeyDown: this.toggleStarSelectedNotes,
        }));
        this.disposers.push(application.itemControllerGroup.addActiveControllerChangeObserver(() => {
            const controllers = application.itemControllerGroup.itemControllers;
            const activeNoteUuids = controllers.map((controller) => controller.item.uuid);
            const selectedUuids = this.getSelectedNotesList().map((n) => n.uuid);
            for (const selectedId of selectedUuids) {
                if (!activeNoteUuids.includes(selectedId)) {
                    application.itemListController.deselectItem({ uuid: selectedId });
                }
            }
        }));
    }
    async handleEvent(event) {
        if (event.type === ApplicationEvent.PreferencesChanged) {
            this.shouldLinkToParentFolders = this.application.preferences.getValue(PrefKey.NoteAddToParentFolders, PrefDefaults[PrefKey.NoteAddToParentFolders]);
        }
        else if (event.type === CrossControllerEvent.UnselectAllNotes) {
            this.unselectNotes();
        }
    }
    disposeCommandRegisters() {
        if (this.commandRegisterDisposers.length > 0) {
            for (const dispose of this.commandRegisterDisposers) {
                dispose();
            }
        }
    }
    deinit() {
        super.deinit();
        this.lastSelectedNote = undefined;
        destroyAllObjectProperties(this);
    }
    get selectedNotes() {
        return this.application.itemListController.getFilteredSelectedItems(ContentType.TYPES.Note);
    }
    get firstSelectedNote() {
        return Object.values(this.selectedNotes)[0];
    }
    get selectedNotesCount() {
        if (this.dealloced) {
            return 0;
        }
        return Object.keys(this.selectedNotes).length;
    }
    get trashedNotesCount() {
        return this.application.items.trashedItems.length;
    }
    setContextMenuClickLocation(location) {
        this.contextMenuClickLocation = location;
    }
    async changeSelectedNotes(mutate) {
        await this.application.mutator.changeItems(this.getSelectedNotesList(), mutate, MutationType.NoUpdateUserTimestamps);
        this.application.sync.sync().catch(console.error);
    }
    setHideSelectedNotePreviews(hide) {
        this.changeSelectedNotes((mutator) => {
            mutator.hidePreview = hide;
        }).catch(console.error);
    }
    setLockSelectedNotes(lock) {
        this.changeSelectedNotes((mutator) => {
            mutator.locked = lock;
        }).catch(console.error);
    }
    async setTrashSelectedNotes(trashed) {
        if (trashed) {
            const notesDeleted = await this.deleteNotes(false);
            if (notesDeleted) {
                runInAction(() => {
                    this.contextMenuOpen = false;
                });
            }
        }
        else {
            await this.changeSelectedNotes((mutator) => {
                mutator.trashed = trashed;
            });
            runInAction(() => {
                this.contextMenuOpen = false;
            });
        }
    }
    async deleteNotesPermanently() {
        await this.deleteNotes(true);
    }
    async deleteNotes(permanently) {
        if (this.getSelectedNotesList().some((note) => note.locked)) {
            const text = StringDeleteLockedNotesAttempt(this.selectedNotesCount);
            this.application.alerts.alert(text).catch(console.error);
            return false;
        }
        const title = permanently ? StringDeleteItemsPermanentlyTitle() : StringTrashItemsTitle();
        let noteTitle = undefined;
        if (this.selectedNotesCount === 1) {
            const selectedNote = this.getSelectedNotesList()[0];
            noteTitle = selectedNote.title.length ? `'${selectedNote.title}'` : c('B3.Notes.NoteActions.Label').t `this note`;
        }
        const text = StringDeleteNotes(permanently, this.selectedNotesCount, noteTitle);
        if (await confirmDialog({
            title,
            text,
            confirmButtonStyle: 'danger',
        })) {
            this.application.itemListController.selectNextItem();
            if (permanently) {
                await this.application.mutator.deleteItems(this.getSelectedNotesList());
                void this.application.sync.sync();
            }
            else {
                await this.changeSelectedNotes((mutator) => {
                    mutator.trashed = true;
                });
            }
            return true;
        }
        return false;
    }
    setPinSelectedNotes(pinned) {
        this.changeSelectedNotes((mutator) => {
            mutator.pinned = pinned;
        }).catch(console.error);
    }
    setStarSelectedNotes(starred) {
        this.changeSelectedNotes((mutator) => {
            mutator.starred = starred;
        }).catch(console.error);
    }
    async setArchiveSelectedNotes(archived) {
        if (this.getSelectedNotesList().some((note) => note.locked)) {
            this.application.alerts
                .alert(StringArchiveLockedNotesAttempt(archived, this.selectedNotesCount))
                .catch(console.error);
            return;
        }
        await this.changeSelectedNotes((mutator) => {
            mutator.archived = archived;
        });
        runInAction(() => {
            this.application.itemListController.deselectAll();
            this.contextMenuOpen = false;
        });
    }
    async toggleArchiveSelectedNotes() {
        const notes = this.selectedNotes;
        const archived = notes.some((note) => note.archived);
        if (!archived) {
            await this.setArchiveSelectedNotes(true);
        }
        else {
            await this.setArchiveSelectedNotes(false);
        }
    }
    async setProtectSelectedNotes(protect) {
        const selectedNotes = this.getSelectedNotesList();
        if (protect) {
            await this.application.protections.protectNotes(selectedNotes);
            this.setShowProtectedWarning(true);
        }
        else {
            await this.application.protections.unprotectNotes(selectedNotes);
            this.setShowProtectedWarning(false);
        }
        void this.application.sync.sync();
    }
    unselectNotes() {
        this.application.itemListController.deselectAll();
    }
    getSpellcheckStateForNote(note) {
        return note.spellcheck != undefined ? note.spellcheck : this._isGlobalSpellcheckEnabled.execute().getValue();
    }
    async toggleGlobalSpellcheckForNote(note) {
        await this.application.mutator.changeItem(note, (mutator) => {
            mutator.toggleSpellcheck();
        }, MutationType.NoUpdateUserTimestamps);
        this.application.sync.sync().catch(console.error);
    }
    getEditorWidthForNote(note) {
        var _a;
        return ((_a = note.editorWidth) !== null && _a !== void 0 ? _a : this.application.preferences.getLocalValue(LocalPrefKey.EditorLineWidth, PrefDefaults[LocalPrefKey.EditorLineWidth]));
    }
    async setNoteEditorWidth(note, editorWidth) {
        await this.application.mutator.changeItem(note, (mutator) => {
            mutator.editorWidth = editorWidth;
        }, MutationType.NoUpdateUserTimestamps);
        this.application.sync.sync().catch(console.error);
    }
    async addTagToSelectedNotes(tag) {
        const selectedNotes = this.getSelectedNotesList();
        await Promise.all(selectedNotes.map(async (note) => {
            await this.application.mutator.addTagToNote(note, tag, this.shouldLinkToParentFolders);
        }));
        this.application.sync.sync().catch(console.error);
    }
    async removeTagFromSelectedNotes(tag) {
        const selectedNotes = this.getSelectedNotesList();
        await this.application.mutator.changeItem(tag, (mutator) => {
            for (const note of selectedNotes) {
                mutator.removeItemAsRelationship(note);
            }
        });
        this.application.sync.sync().catch(console.error);
    }
    isTagInSelectedNotes(tag) {
        const selectedNotes = this.getSelectedNotesList();
        return selectedNotes.every((note) => this._getItemTags
            .execute(note)
            .getValue()
            .find((noteTag) => noteTag.uuid === tag.uuid));
    }
    setShowProtectedWarning(show) {
        this.showProtectedWarning = show;
    }
    async emptyTrash() {
        if (await confirmDialog({
            text: StringEmptyTrash(this.trashedNotesCount),
            confirmButtonStyle: 'danger',
        })) {
            await this.application.mutator.emptyTrash();
            this.application.sync.sync().catch(console.error);
        }
    }
    getSelectedNotesList() {
        return Object.values(this.selectedNotes);
    }
    async createNoteWithContent(editorIdentifier, title, text, references = []) {
        const noteType = noteTypeForEditorIdentifier(editorIdentifier);
        const selectedTag = this.application.navigationController.selected;
        const templateNote = this.application.items.createTemplateItem(ContentType.TYPES.Note, {
            title,
            text,
            references,
            noteType,
            editorIdentifier,
        });
        const note = await this.application.mutator.insertItem(templateNote);
        if (selectedTag instanceof SNTag) {
            const shouldAddTagHierarchy = this.application.preferences.getValue(PrefKey.NoteAddToParentFolders, true);
            await this.application.mutator.addTagToNote(templateNote, selectedTag, shouldAddTagHierarchy);
        }
        return note;
    }
}
